
// src/components/configuracion/CatalogosTab/CatalogosPresentation.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon
} from '@mui/icons-material';

// Componentes compartidos
import { 
  CardContainer, 
  TabPanel, 
  DataTable, 
  EmptyState, 
  LoadingState, 
  ConfirmDialog, 
  FormField 
} from '../../shared';

/**
 * Componente presentacional para mostrar los catálogos
 * Solo se encarga de la UI, no contiene lógica de negocio
 */
const CatalogosPresentation = ({
  // Estado general
  empresaActiva,
  tabValue,
  onTabChange,
  
  // Cuentas
  cuentas,
  loadingCuentas,
  errorCuentas,
  cuentasColumns,
  cuentasActions,
  openCuentaDialog,
  onCloseCuentaDialog,
  onOpenCuentaDialog,
  onGuardarCuenta,
  cuentaSeleccionada,
  guardandoCuenta,
  cuentaForm,
  
  // Centros
  centros,
  loadingCentros,
  errorCentros,
  centrosColumns,
  centrosActions,
  openCentroDialog,
  onCloseCentroDialog,
  onOpenCentroDialog,
  onGuardarCentro,
  centroSeleccionado,
  guardandoCentro,
  centroForm,
  
  // Clasificadores
  clasificadores,
  loadingClasificadores,
  errorClasificadores,
  clasificadoresColumns,
  clasificadoresActions,
  openClasificadorDialog,
  onCloseClasificadorDialog,
  onOpenClasificadorDialog,
  onGuardarClasificador,
  clasificadorSeleccionado,
  guardandoClasificador,
  clasificadorForm
}) => {
  return (
    <CardContainer
      title="Catálogos Contables"
      subheader={`Gestión de cuentas, centros y clasificadores para ${empresaActiva ? empresaActiva.nombre_corto || empresaActiva.razon_social : 'su empresa'}`}
    >
      {!empresaActiva ? (
        <EmptyState
          title="No hay empresa seleccionada"
          message="Debe seleccionar una empresa para gestionar sus catálogos contables."
        />
      ) : (
        <Paper elevation={0}>
          <Tabs
            value={tabValue}
            onChange={onTabChange}
            indicatorColor="primary"
            textColor="primary"
            centered
          >
            <Tab label="Cuentas Contables" />
            <Tab label="Centros de Costo" />
            <Tab label="Clasificadores" />
          </Tabs>
          
          {/* Pestaña de Cuentas */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Cuentas Contables</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => onOpenCuentaDialog()}
              >
                Nueva Cuenta
              </Button>
            </Box>
            
            <LoadingState 
              loading={loadingCuentas} 
              error={errorCuentas}
            >
              {cuentas && cuentas.length > 0 ? (
                <DataTable
                  columns={cuentasColumns}
                  data={cuentas}
                  actions={cuentasActions}
                  pagination={true}
                />
              ) : (
                <EmptyState
                  title="No hay cuentas contables definidas"
                  message="No se han encontrado cuentas contables para esta empresa. Puede crear una nueva cuenta usando el botón 'Nueva Cuenta'."
                  actionText="Nueva Cuenta" 
                  onAction={() => onOpenCuentaDialog()}
                  usePaper={true}
                />
              )}
            </LoadingState>
          </TabPanel>
          
          {/* Pestaña de Centros */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Centros de Costo</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => onOpenCentroDialog()}
              >
                Nuevo Centro
              </Button>
            </Box>
            
            <LoadingState 
              loading={loadingCentros} 
              error={errorCentros}
            >
              {centros && centros.length > 0 ? (
                <DataTable
                  columns={centrosColumns}
                  data={centros}
                  actions={centrosActions}
                  pagination={true}
                />
              ) : (
                <EmptyState
                  title="No hay centros de costo definidos"
                  message="No se han encontrado centros de costo para esta empresa. Puede crear un nuevo centro usando el botón 'Nuevo Centro'."
                  actionText="Nuevo Centro" 
                  onAction={() => onOpenCentroDialog()}
                  usePaper={true}
                />
              )}
            </LoadingState>
          </TabPanel>
          
          {/* Pestaña de Clasificadores */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Clasificadores</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => onOpenClasificadorDialog()}
              >
                Nuevo Clasificador
              </Button>
            </Box>
            
            <LoadingState 
              loading={loadingClasificadores} 
              error={errorClasificadores}
            >
              {clasificadores && clasificadores.length > 0 ? (
                <DataTable
                  columns={clasificadoresColumns}
                  data={clasificadores}
                  actions={clasificadoresActions}
                  pagination={true}
                />
              ) : (
                <EmptyState
                  title="No hay clasificadores definidos"
                  message="No se han encontrado clasificadores para esta empresa. Puede crear un nuevo clasificador usando el botón 'Nuevo Clasificador'."
                  actionText="Nuevo Clasificador" 
                  onAction={() => onOpenClasificadorDialog()}
                  usePaper={true}
                />
              )}
            </LoadingState>
          </TabPanel>
        </Paper>
      )}
      
      {/* Diálogo de Cuenta */}
      <ConfirmDialog
        open={openCuentaDialog}
        onClose={onCloseCuentaDialog}
        onConfirm={onGuardarCuenta}
        title={cuentaSeleccionada ? "Editar Cuenta Contable" : "Nueva Cuenta Contable"}
        content={
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormField
                  type="text"
                  label="Código"
                  name="codigo"
                  value={cuentaForm.values.codigo}
                  onChange={cuentaForm.handleChange}
                  onBlur={cuentaForm.handleBlur}
                  error={cuentaForm.touched.codigo && cuentaForm.errors.codigo}
                  required
                  disabled={guardandoCuenta}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <FormField
                  type="text"
                  label="Nombre"
                  name="nombre"
                  value={cuentaForm.values.nombre}
                  onChange={cuentaForm.handleChange}
                  onBlur={cuentaForm.handleBlur}
                  error={cuentaForm.touched.nombre && cuentaForm.errors.nombre}
                  required
                  disabled={guardandoCuenta}
                />
              </Grid>
              <Grid item xs={12}>
                <FormField
                  type="select"
                  label="Tipo"
                  name="tipo"
                  value={cuentaForm.values.tipo}
                  onChange={cuentaForm.handleChange}
                  disabled={guardandoCuenta}
                  options={[
                    { value: '', label: 'Sin tipo' },
                    { value: 'ACTIVO', label: 'Activo' },
                    { value: 'PASIVO', label: 'Pasivo' },
                    { value: 'PATRIMONIO', label: 'Patrimonio' },
                    { value: 'RESULTADO', label: 'Resultado' }
                  ]}
                />
              </Grid>
            </Grid>
          </Box>
        }
        loading={guardandoCuenta}
        confirmText="Guardar"
      />
      
      {/* Diálogo de Centro */}
      <ConfirmDialog
        open={openCentroDialog}
        onClose={onCloseCentroDialog}
        onConfirm={onGuardarCentro}
        title={centroSeleccionado ? "Editar Centro de Costo" : "Nuevo Centro de Costo"}
        content={
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormField
                  type="text"
                  label="Código"
                  name="codigo"
                  value={centroForm.values.codigo}
                  onChange={centroForm.handleChange}
                  onBlur={centroForm.handleBlur}
                  error={centroForm.touched.codigo && centroForm.errors.codigo}
                  required
                  disabled={guardandoCentro}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <FormField
                  type="text"
                  label="Nombre"
                  name="nombre"
                  value={centroForm.values.nombre}
                  onChange={centroForm.handleChange}
                  onBlur={centroForm.handleBlur}
                  error={centroForm.touched.nombre && centroForm.errors.nombre}
                  required
                  disabled={guardandoCentro}
                />
              </Grid>
            </Grid>
          </Box>
        }
        loading={guardandoCentro}
        confirmText="Guardar"
      />
      
      {/* Diálogo de Clasificador */}
      <ConfirmDialog
        open={openClasificadorDialog}
        onClose={onCloseClasificadorDialog}
        onConfirm={onGuardarClasificador}
        title={clasificadorSeleccionado ? "Editar Clasificador" : "Nuevo Clasificador"}
        content={
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormField
                  type="text"
                  label="Código"
                  name="codigo"
                  value={clasificadorForm.values.codigo}
                  onChange={clasificadorForm.handleChange}
                  onBlur={clasificadorForm.handleBlur}
                  error={clasificadorForm.touched.codigo && clasificadorForm.errors.codigo}
                  required
                  disabled={guardandoClasificador}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <FormField
                  type="text"
                  label="Nombre"
                  name="nombre"
                  value={clasificadorForm.values.nombre}
                  onChange={clasificadorForm.handleChange}
                  onBlur={clasificadorForm.handleBlur}
                  error={clasificadorForm.touched.nombre && clasificadorForm.errors.nombre}
                  required
                  disabled={guardandoClasificador}
                />
              </Grid>
              <Grid item xs={12}>
                <FormField
                  type="select"
                  label="Tipo"
                  name="tipo"
                  value={clasificadorForm.values.tipo}
                  onChange={clasificadorForm.handleChange}
                  disabled={guardandoClasificador}
                  options={[
                    { value: '', label: 'Sin tipo' },
                    { value: 'GASTO', label: 'Gasto' },
                    { value: 'INGRESO', label: 'Ingreso' },
                    { value: 'ACTIVO', label: 'Activo' },
                    { value: 'PASIVO', label: 'Pasivo' }
                  ]}
                />
              </Grid>
            </Grid>
          </Box>
        }
        loading={guardandoClasificador}
        confirmText="Guardar"
      />
    </CardContainer>
  );
};

CatalogosPresentation.propTypes = {
  // Estado general
  empresaActiva: PropTypes.object,
  tabValue: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  
  // Cuentas
  cuentas: PropTypes.array,
  loadingCuentas: PropTypes.bool,
  errorCuentas: PropTypes.string,
  cuentasColumns: PropTypes.array.isRequired,
  cuentasActions: PropTypes.array.isRequired,
  openCuentaDialog: PropTypes.bool.isRequired,
  onCloseCuentaDialog: PropTypes.func.isRequired,
  onOpenCuentaDialog: PropTypes.func.isRequired,
  onGuardarCuenta: PropTypes.func.isRequired,
  cuentaSeleccionada: PropTypes.object,
  guardandoCuenta: PropTypes.bool,
  cuentaForm: PropTypes.object.isRequired,
  
  // Centros
  centros: PropTypes.array,
  loadingCentros: PropTypes.bool,
  errorCentros: PropTypes.string,
  centrosColumns: PropTypes.array.isRequired,
  centrosActions: PropTypes.array.isRequired,
  openCentroDialog: PropTypes.bool.isRequired,
  onCloseCentroDialog: PropTypes.func.isRequired,
  onOpenCentroDialog: PropTypes.func.isRequired,
  onGuardarCentro: PropTypes.func.isRequired,
  centroSeleccionado: PropTypes.object,
  guardandoCentro: PropTypes.bool,
  centroForm: PropTypes.object.isRequired,
  
  // Clasificadores
  clasificadores: PropTypes.array,
  loadingClasificadores: PropTypes.bool,
  errorClasificadores: PropTypes.string,
  clasificadoresColumns: PropTypes.array.isRequired,
  clasificadoresActions: PropTypes.array.isRequired,
  openClasificadorDialog: PropTypes.bool.isRequired,
  onCloseClasificadorDialog: PropTypes.func.isRequired,
  onOpenClasificadorDialog: PropTypes.func.isRequired,
  onGuardarClasificador: PropTypes.func.isRequired,
  clasificadorSeleccionado: PropTypes.object,
  guardandoClasificador: PropTypes.bool,
  clasificadorForm: PropTypes.object.isRequired
};

export default CatalogosPresentation;